import { Context } from '../..';
import { ReportType } from '../../../models/overmind/report-type';
import * as base from '../base';
import { UserStatusReport } from '../../../models/user';
import { NotificationPriority, NotificationType } from '../../../notifications/notifications.models';
import { RequiredEntity } from '../../../models/view/entity';
import { downloadCSV } from '../../../library/helpers/download-csv';

const CONTROLLER = `report`;

export async function downloadReport(context: Context, reportTypeId: number): Promise<string | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `download-report`, reportTypeId.toString()), authenticated: true };
  const response: base.IResponse<string> = await base.request(request);

  return response.data;
}

export async function getAdministrationReportTemplates(context: Context, companySlug: string): Promise<ReportType[] | undefined> {
  const url = base.apiURL(`reporttemplate`, `report-templates`, companySlug);

  const request: base.IRequest = { url: url, authenticated: true };
  const response: base.IResponse<ReportType[]> = await base.request(request);

  return response.data;
}

export async function getAnalyticsReportTemplates(context: Context, companySlug: string): Promise<ReportType[] | undefined> {
  const url = base.apiURL(CONTROLLER, `report-templates`, companySlug);

  const request: base.IRequest = { url: url, authenticated: true };
  const response: base.IResponse<ReportType[]> = await base.request(request);

  return response.data;
}

export async function addReportTemplate(context: Context, payload: { companySlug: string; reportType: string }): Promise<base.IResponse<ReportType> | undefined> {
  const url = base.apiURL(`reporttemplate`, `company-report-template`, payload.companySlug, payload.reportType);
  const request: base.IRequest = { url: url, method: 'POST', authenticated: true };
  const response: base.IResponse<ReportType> = await base.request(request);

  if (!!response.error) {
    context.actions.addNotification({
      id: payload.reportType,
      type: NotificationType.Error,
      priority: NotificationPriority.Toast,
      title: '',
      message: `${response.error.type}. Please contact support`,
      active: true,
    });
  }

  return response;
}

export async function removeReportTemplate(context: Context, payload: { companySlug: string; reportType: string }): Promise<base.IResponse<ReportType> | undefined> {
  const url = base.apiURL(`reporttemplate`, `company-report-template`, payload.companySlug, payload.reportType);
  const request: base.IRequest = { url: url, method: 'DELETE', authenticated: true };
  const response: base.IResponse<ReportType> = await base.request(request);

  if (!!response.error) {
    context.actions.addNotification({
      id: payload.reportType,
      type: NotificationType.Error,
      priority: NotificationPriority.Toast,
      title: '',
      message: `${response.error.type}. Please contact support`,
      active: true,
    });
  }

  return response;
}

export async function editReportTemplate(context: Context, payload: { companySlug: string; reportType: string; body: { name: string; description: string } }) {
  const url = base.apiURL(`reporttemplate`, `company-report-template`, payload.companySlug, payload.reportType);
  const request: base.IRequest = { url: url, method: 'PUT', body: JSON.stringify(payload.body), authenticated: true };
  const response: base.IResponse<ReportType> = await base.request(request);

  if (!!response.error) {
    context.actions.addNotification({
      id: payload.reportType,
      type: NotificationType.Error,
      priority: NotificationPriority.Toast,
      title: '',
      message: `${response.error.type}. Please contact support`,
      active: true,
    });
  } else {
    context.actions.addNotification({
      id: Date.now().toString(),
      message: 'Report details updated successfully',
      title: '',
      type: NotificationType.Success,
      priority: NotificationPriority.Toast,
      active: true,
    });
  }
}

export async function userStatusReport(_: Context, companySlug: string): Promise<UserStatusReport[] | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `view-report`, `user-status${base.params({ companySlug: companySlug })}`), authenticated: true };
  const response: base.IResponse<UserStatusReport[]> = await base.request(request);

  return response.data;
}

export async function getPDFReports(): Promise<RequiredEntity[] | undefined> {
  const request: base.IRequest = { url: base.apiURL('pdfreport', 'pdf-list'), authenticated: true };
  const response: base.IResponse<RequiredEntity[]> = await base.request(request);

  return response.data;
}

export async function downloadReportTemplateCSV({ state }: Context, payload: string): Promise<void> {
  const url = base.apiURL(CONTROLLER, 'download-report', `${payload}${base.params({ companySlug: state.companyVariables.slug })}`);
  const headers = { 'Content-Type': 'text/csv' };
  const request: base.IRequest = { url, headers };
  const response: base.IResponse<string> = await base.request(request);
  if (!response.data) return;

  await downloadCSV(response.data, `report-template-${payload}`);
  return;
}
